import { allByCssHtml, byCssHtml, changeStyleClass, closestElement, toHtmlElements } from "@de-xima/xima-common-js-dom";
import {
  assertInstance,
  identity,
  isEmpty,
  notUndefined,
  parseString,
  reduceIterable,
  substringBeforeLast,
  toMap,
} from "@de-xima/xima-common-js-lang";

import { findWidgetForElement } from "./Util.ts";

export function onIconClicked(event: Event): boolean {
  const dom = acquireDomForEvent(event);
  const button = findTargetButton(event);
  if (button === undefined) {
    console.warn("SelectIcon: Unable to find button");
    return false;
  }

  if (dom.hiddenInputEl.disabled || dom.hiddenInputEl.readOnly) {
    return false;
  }

  const currentValue = getIconValue(dom.hiddenInput.jq);
  const clickedValue = parseString(button.jq.attr("data-value"));
  const newValue = currentValue === clickedValue ? "" : clickedValue;

  setIconValue(dom.hiddenInput.jq, newValue);
  dom.overlay.hide();

  return false;
}

export function onClearClicked(event: Event): boolean {
  event.stopPropagation();

  const dom = acquireDomForEvent(event);

  setIconValue(dom.hiddenInput.jq, "");
  dom.overlay.hide();

  return false;
}

export function getIconValue(element: HTMLElement | JQuery): string {
  const el = toHtmlElements(element)[0];
  if (el === undefined) {
    throw new Error("SelectIcon: Unable to find element");
  }
  const dom = acquireDomForElement(el);
  return parseString(dom.hiddenInput.jq.val());
}

export function setIconValue(element: HTMLElement | JQuery, value: string | undefined): void {
  const el = toHtmlElements(element)[0];
  if (el === undefined) {
    throw new Error("SelectIcon: Unable to find element");
  }
  const dom = acquireDomForElement(el);

  dom.hiddenInput.jq.val(value ?? "");

  for (const [key, button] of dom.icons.entries()) {
    changeStyleClass(button.jq, "select-icon__button--selected", key === value);
  }

  changeStyleClass(dom.current, "select-icon__current--empty", isEmpty(value));
  dom.current.children[0]?.setAttribute("class", value ?? "");
}

function findTargetButton(event: Event): PrimeFaces.widget.Button | undefined {
  const element = event.target;
  assertInstance(element, HTMLElement);
  return findWidgetForElement(element, PrimeFaces.widget.Button);
}

interface Dom {
  current: HTMLElement;
  icons: Map<string, PrimeFaces.widget.Button>;
  hiddenInput: PrimeFaces.widget.InputText;
  hiddenInputEl: HTMLInputElement;
  overlay: PrimeFaces.widget.OverlayPanel;
}

function acquireDomForEvent(event: Event): Dom {
  const element = event.target;
  assertInstance(element, HTMLElement);
  return acquireDomForElement(element);
}

function acquireDomForElement(element: HTMLElement): Dom {
  const container = closestElement(element, ".select-icon,.select-icon__overlay");
  if (container === undefined) {
    throw new Error("SelectIcon: Unable to find container");
  }

  const baseId = substringBeforeLast(container.id, ":");
  const mainElement = document.getElementById(`${baseId}:selectIcon`);
  const overlayElement = document.getElementById(`${baseId}:overlay`);

  if (mainElement === null || overlayElement === null) {
    throw new Error("SelectIcon: Unable to find main element or overlay element");
  }

  const hiddenInput = findWidgetForElement(
    byCssHtml(".select-icon__hidden-value-input", mainElement),
    PrimeFaces.widget.InputText,
  );
  const hiddenInputEl = hiddenInput?.jq.get(0);

  const icons = reduceIterable(
    allByCssHtml(".select-icon__button", overlayElement)
      .map((x) => findWidgetForElement(x, PrimeFaces.widget.Button))
      .filter(notUndefined),
    toMap((x) => x.jq.attr("data-value") ?? "", identity),
  );

  const overlay = findWidgetForElement(overlayElement, PrimeFaces.widget.OverlayPanel);

  const current = byCssHtml(".select-icon__current", mainElement);

  if (hiddenInput === undefined) {
    throw new Error("SelectIcon: Unable to find hidden input");
  }
  if (!(hiddenInputEl instanceof HTMLInputElement)) {
    throw new Error("SelectIcon: Hidden input is not an input element");
  }
  if (overlay === undefined) {
    throw new Error("SelectIcon: Unable to find overlay panel");
  }
  if (current === undefined) {
    throw new Error("SelectIcon: Unable to find element for current value");
  }

  return { current, icons, hiddenInput, hiddenInputEl, overlay };
}
